import type { CompanyListItemResource } from '@majuscule/contracts';
import { useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { cityOf, filterCompanies, searchKey } from '../../lib/company-search';

/** Results shown at once: beyond, the operator types a few more letters. */
const MAX_RESULTS = 50;

/**
 * Champ de recherche d'entreprise (motif ARIA « combobox ») : la liste se
 * filtre pendant la frappe ; flèches, Entrée et Échap au clavier. Tant
 * qu'aucune entreprise n'est choisie dans la liste, la valeur vaut `''`.
 */
export function CompanyCombobox({
  companies,
  value,
  onChange,
  loading = false,
  placeholder = 'Rechercher une entreprise (nom, SIREN, ville)',
}: {
  companies: readonly CompanyListItemResource[];
  /** Identifiant de l'entreprise choisie, `''` si aucune. */
  value: string;
  onChange: (companyId: string) => void;
  loading?: boolean;
  placeholder?: string;
}): JSX.Element {
  const selected = companies.find((company) => company.id === value);
  const [text, setText] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Le nom s'affiche quand la liste arrive après le choix initial (entreprise passée en paramètre).
  const [shownFor, setShownFor] = useState(selected?.id);
  if (selected !== undefined && selected.id !== shownFor) {
    setShownFor(selected.id);
    setText(selected.name);
  }

  const results = useMemo(
    () => filterCompanies(companies, selected !== undefined && text === selected.name ? '' : text),
    [companies, selected, text],
  );
  const visible = results.slice(0, MAX_RESULTS);
  const activeIndex = Math.min(active, Math.max(visible.length - 1, 0));
  const optionId = (index: number): string => `${listId}-option-${index}`;

  const choose = (company: CompanyListItemResource): void => {
    setText(company.name);
    setShownFor(company.id);
    setOpen(false);
    onChange(company.id);
  };

  const scrollTo = (index: number): void => {
    listRef.current?.querySelector(`#${CSS.escape(optionId(index))}`)?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = Math.min(Math.max(activeIndex + step, 0), Math.max(visible.length - 1, 0));
        setActive(next);
        scrollTo(next);
        return;
      }
      case 'Enter': {
        const company = visible[activeIndex];
        if (open && company !== undefined) {
          // Entrée choisit l'entreprise surlignée au lieu d'envoyer le formulaire.
          event.preventDefault();
          choose(company);
        }
        return;
      }
      case 'Escape':
        if (open) {
          // Ferme la liste sans fermer la fenêtre de dialogue.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        return;
      default:
        return;
    }
  };

  return (
    <Popover open={open && !loading} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative w-full min-w-0">
          <Input
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={open && visible.length > 0 ? optionId(activeIndex) : undefined}
            placeholder={loading ? 'Chargement des entreprises…' : placeholder}
            disabled={loading}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setActive(0);
              setOpen(true);
              if (value !== '') {
                onChange('');
              }
            }}
            onFocus={() => {
              setOpen(true);
            }}
            onClick={() => {
              setOpen(true);
            }}
            onBlur={() => {
              // Un nom tapé en entier vaut choix, s'il ne désigne qu'une entreprise.
              if (value === '') {
                const exact = companies.filter((company) => searchKey(company.name) === searchKey(text));
                const only = exact[0];
                if (exact.length === 1 && only !== undefined) {
                  choose(only);
                }
              }
            }}
            onKeyDown={onKeyDown}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        onOpenAutoFocus={(event) => {
          // Le focus reste dans le champ : on continue de taper.
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          // Un clic dans le champ ne doit pas refermer la liste qu'il vient d'ouvrir.
          if (event.target instanceof Node && anchorRef.current?.contains(event.target) === true) {
            event.preventDefault();
          }
        }}
      >
        {visible.length === 0 ? (
          <p className="m-0 px-2 py-1.5 text-[13px] text-muted-foreground">
            Aucune entreprise ne correspond. Créez-la d’abord depuis la page Entreprises.
          </p>
        ) : (
          <ul ref={listRef} id={listId} role="listbox" className="m-0 max-h-64 list-none overflow-auto p-0">
            {visible.map((company, index) => (
              <li
                key={company.id}
                id={optionId(index)}
                role="option"
                aria-selected={company.id === value}
                className={cn(
                  'flex cursor-pointer items-baseline justify-between gap-3 rounded-md px-2 py-1.5 text-[13px] leading-[18px]',
                  index === activeIndex && 'bg-accent',
                )}
                onMouseDown={(event) => {
                  // Garde le focus dans le champ jusqu'au choix.
                  event.preventDefault();
                }}
                onMouseMove={() => {
                  if (index !== activeIndex) setActive(index);
                }}
                onClick={() => {
                  choose(company);
                }}
              >
                <span className="min-w-0 truncate font-medium">{company.name}</span>
                <span className="shrink-0 truncate text-caption text-muted-foreground">
                  {[company.siren, cityOf(company.address)].filter((part) => part !== null && part !== '').join(' · ')}
                </span>
              </li>
            ))}
            {results.length > MAX_RESULTS ? (
              <li role="presentation" className="px-2 py-1.5 text-caption text-muted-foreground">
                {results.length - MAX_RESULTS} autres : précisez la recherche.
              </li>
            ) : null}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
